import { Link, NavLink } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useMe } from "@/context/MeContext";
import { Logo } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";

const FIRMWARE = { to: "/firmware", label: "Firmware setup", id: "nav-firmware-link" };
const PROJECTS = { to: "/projects", label: "My projects", id: "nav-projects-link" };
const SETUP = { to: "/try", label: "Set up a robot", id: "nav-setup-robot-link" };
const ADMIN = { to: "/admin", label: "Admin", id: "nav-admin-link" };
// Classroom pairing (/classroom) is hidden from the menus until further notice; the page itself still works.
const LINKS = [
  { to: "/how-it-works", label: "How it works", id: "nav-how-it-works-link" },
  { to: "/compatibility", label: "Compatibility", id: "nav-compatibility-link" },
];

const useIsAdmin = () => useMe().me?.role === "admin";

const Item = ({ to, label, id }) => (
  <NavLink
    to={to}
    data-testid={id}
    className={({ isActive }) =>
      `whitespace-nowrap rounded-full px-3.5 py-2 text-sm font-medium transition-colors ${isActive ? "bg-secondary text-secondary-foreground" : "text-muted-foreground hover:text-foreground"}`
    }
  >
    {label}
  </NavLink>
);

const MobileItem = ({ to, label, id }) => (
  <DropdownMenuItem asChild><Link to={to} data-testid={`mobile-${id}`}>{label}</Link></DropdownMenuItem>
);

const MobileMenu = () => {
  const isAdmin = useIsAdmin();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="xl:hidden" data-testid="mobile-menu-button" aria-label="Open menu">
          <Menu className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <MobileItem {...FIRMWARE} />
        <Show when="signed-in"><MobileItem {...PROJECTS} /></Show>
        <Show when="signed-out"><MobileItem {...SETUP} /></Show>
        {LINKS.map((l) => <MobileItem key={l.to} {...l} />)}
        {isAdmin && <MobileItem {...ADMIN} />}
        <Show when="signed-out">
          <DropdownMenuSeparator />
          <DropdownMenuItem asChild><Link to="/sign-in" data-testid="mobile-nav-sign-in">Sign in</Link></DropdownMenuItem>
          <DropdownMenuItem asChild><Link to="/try" data-testid="mobile-nav-sign-up">Start free</Link></DropdownMenuItem>
        </Show>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const DesktopNav = () => {
  const isAdmin = useIsAdmin();
  return (
    <nav className="hidden items-center gap-1 xl:flex">
      <Item {...FIRMWARE} />
      <Show when="signed-in"><Item {...PROJECTS} /></Show>
      <Show when="signed-out"><Item {...SETUP} /></Show>
      {LINKS.map((l) => <Item key={l.to} {...l} />)}
      {isAdmin && <Item {...ADMIN} />}
    </nav>
  );
};

export const Header = () => (
  <header className="no-print sticky top-0 z-40 border-b bg-background/85 backdrop-blur-xl">
    <div className="mx-auto flex h-16 max-w-7xl items-center gap-6 px-4 sm:px-8">
      <Logo />
      <DesktopNav />
      <div className="ml-auto flex items-center gap-2">
        <ThemeToggle />
        <Show when="signed-out">
          <Button asChild variant="ghost" className="hidden sm:inline-flex rounded-full">
            <Link to="/sign-in" data-testid="header-sign-in-button">Sign in</Link>
          </Button>
          <Button asChild className="hidden rounded-full bg-accent text-accent-foreground hover:bg-accent/90 sm:inline-flex">
            <Link to="/try" data-testid="header-sign-up-button">Start free</Link>
          </Button>
        </Show>
        <Show when="signed-in">
          <div data-testid="user-menu" className="flex items-center"><UserButton /></div>
        </Show>
        <MobileMenu />
      </div>
    </div>
  </header>
);
